import { createSlice, createAsyncThunk, createEntityAdapter, EntityState } from '@reduxjs/toolkit';
import { AsyncStatus, AsyncStatusEnum } from '../model/common/AsyncStatus';
import Document from '../model/common/Document';
import api from '../utils/api';
import fileDownloader from '../utils/fileDownloader';
import { ApplicationState } from '.';
import ErrorResponse from '../model/common/ErrorResponse';

export const fetchDocuments = createAsyncThunk('documents/fetchDocuments', () => {
  return api.get<Document[]>(`api/v1/documents`);
});

export const createDocument = createAsyncThunk('documents/createDocument', ({documentType, document, description, name}: {
  documentType: string,
  document: File,
  description?: string,
  name?: string
}) => {
  const formData = new FormData();
  formData.append("Document", document, document.name);
  formData.append("DocumentType", documentType);
  if (description != undefined) {
    formData.append("Description", description);
  }
  if (name != undefined) {
    formData.append("Name", name);
  }
  return api.postHCSResponse<Document>('api/v1/documents', {
    body: formData
  });
});

export const deleteDocument = createAsyncThunk('documents/deleteDocument', async ({id}: {id: number}) => {
  await api.delete(`api/v1/documents/${id}`);
  return id;
})

export const getDocument = createAsyncThunk('documents/getDocument', ({id}: {id: number}) => {
  return fileDownloader.downloadFile(`api/v1/documents/${id}/download`);
});

const documentsAdapter = createEntityAdapter<Document>();

declare type DocumentsState = EntityState<Document> & {
  fetchStatus: AsyncStatus,
  fetchError?: string,
  createStatus: AsyncStatus,
  createError?: string[]
}

const initialState: DocumentsState = documentsAdapter.getInitialState({
  fetchStatus: AsyncStatusEnum.IDLE,
  createStatus: AsyncStatusEnum.IDLE
});

const documentsSlice = createSlice({
    name: 'documents',
    initialState,
    reducers: {
      documentRemoved: (state, action) => {
        documentsAdapter.removeOne(state, action.payload);
      }
    },
    extraReducers: {
      [fetchDocuments.pending.toString()]: (state, action) => {
        state.fetchStatus = AsyncStatusEnum.LOADING;
        state.fetchError = undefined;
      },
      [fetchDocuments.fulfilled.toString()]: (state, action) => {
        state.fetchStatus = AsyncStatusEnum.SUCCEEDED;
        state.fetchError = undefined;
        documentsAdapter.setAll(state, action.payload);
      },
      [fetchDocuments.rejected.toString()]: (state, action) => {
        state.fetchStatus = AsyncStatusEnum.FAILED;
        state.fetchError = action.error.message;
      },
      [createDocument.pending.toString()]: (state, action) => {
        state.createStatus = AsyncStatusEnum.LOADING;
        state.createError = undefined;
      },
      [createDocument.fulfilled.toString()]: (state, action) => {
        state.createStatus = AsyncStatusEnum.SUCCEEDED;
        state.createError = undefined;
        documentsAdapter.addOne(state, action.payload);
      },
      [createDocument.rejected.toString()]: (state, action) => {
        state.createStatus = AsyncStatusEnum.FAILED;

        let createError: string[] = [];
        if (action.error.message != undefined)
        {
          try
          {
            let errorResponse = JSON.parse(action.error.message) as ErrorResponse;
            createError = errorResponse.summary;
            state.createError = createError.length == 0 ? undefined : createError;
            return;
          } catch(e){}
        }
        state.createError = action.error.message;
      },
      [deleteDocument.fulfilled.toString()]: (state, action) => {
        documentsAdapter.removeOne(state, action.payload);
      }
    }
});

export default documentsSlice;

const selectors = documentsAdapter.getSelectors<ApplicationState>(state => state.documents);

export const {
  selectAll
} = selectors;
